import { useMemo } from "react";
import { Flipped, Flipper } from "react-flip-toolkit";
import { makeStyles } from "tss-react/mui";
import {
  MiButton,
  MiCard,
  MiReorderButton,
  MiSectionHeader,
} from "@miplatform/components";
import { BUTTON_COLORS, BUTTON_VARIANTS } from "@miplatform/constants";
import { arrayMove } from "@dnd-kit/sortable";
import { mdiMinus, mdiPlus } from "@mdi/js";
import { Box, Divider, Typography } from "@mui/material";
import { ConfigAccordionHeader } from "../../../../../Config/components/ConfigAccordionHeader/ConfigAccordionHeader";
import { MaterialCard } from "../../../../components/MaterialCard/MaterialCard";

const parsePattern = (pattern) => {
  if (!pattern) return [];
  const matches = pattern.match(/\{[^{}]*\}/g) || [];
  return matches.map((match) => {
    const inner = match.replaceAll("{", "").replaceAll("}", "");
    const [label, value] = inner.includes(":") ? inner.split(":") : [inner, ""];
    return { id: label, label, value };
  });
};

const buildPattern = (items) => {
  return items
    .map((item) =>
      item.value ? `{${item.label}:${item.value}}` : `{${item.label}}`
    )
    .join("-");
};

export function CategoryPatternTab({ form, extraTabProps = {} }) {
  const { setValue, watch } = form;
  const { isReadOnly } = extraTabProps;
  const { classes } = useStyles();

  const { itemDescriptionPattern, name, attributes = [] } = watch();

  const items = useMemo(
    () => parsePattern(itemDescriptionPattern),
    [itemDescriptionPattern]
  );

  const removedItems = useMemo(() => {
    const removed = [];
    const activeLabels = new Set(items.map((item) => item.label));
    if (!activeLabels.has("CategoryName")) {
      removed.push({ id: "CategoryName", label: "CategoryName", value: "" });
    }
    attributes
      .filter((attr) => attr.isRequired && !activeLabels.has(attr.name))
      .forEach((attr) =>
        removed.push({ id: attr.name, label: attr.name, value: "" })
      );
    return removed;
  }, [items, attributes]);

  const updatePattern = (item) => {
    setValue("itemDescriptionPattern", buildPattern(item), {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleMoveUp = (index) => {
    if (index > 0) {
      const updatedItems = arrayMove(items, index, index - 1);
      updatePattern(updatedItems);
    }
  };

  const handleMoveDown = (index) => {
    if (index < items.length - 1) {
      const updatedItems = arrayMove(items, index, index + 1);
      updatePattern(updatedItems);
    }
  };

  const handleRemoveItem = (itemId) => {
    const updatedItems = items.filter((item) => item.id !== itemId);
    updatePattern(updatedItems);
  };

  const handleAddBackItem = (item) => {
    const updatedItems = [...items, item];
    updatePattern(updatedItems);
  };

  const handleToggleAbbr = (itemId, isAbbr) => {
    const updatedItems = items.map((item) =>
      item.id === itemId ? { ...item, value: isAbbr ? "Abbr" : "" } : item
    );
    updatePattern(updatedItems);
  };

  const previewItem = useMemo(() => {
    const description = items
      .map((item) => (item.label === "CategoryName" ? "Category" : item.label))
      .join("-");
    return {
      manufacturerPartNumber: "PART NUMBER",
      category: {
        categoryName: name || "Category",
        itemDescription: description,
      },
      attributes: attributes.map((attr) => ({ value: attr.name })),
    };
  }, [items, name, attributes]);

  return (
    <ConfigAccordionHeader title="Card Layout & Preview">
      <Box className={classes.root}>
        <MaterialCard item={previewItem} />
        <MiSectionHeader title="Display Order" variant="text" />
        <MiCard
          title="Item Description"
          containerStyles={{ padding: 16, margin: 0 }}
          headerContainerStyles={{ marginBottom: 0 }}
        >
          <Divider sx={{ marginBottom: 2 }} />
          <Flipper
            flipKey={items.map((item) => item.id).join("-")}
            className={classes.sortableCardContainer}
          >
            {items.map((item, index) => (
              <Flipped key={item.id} flipId={item.id}>
                {(flippedProps) => (
                  <Box {...flippedProps} className={classes.sortableRow}>
                    <Typography variant="body2">{index + 1}.</Typography>
                    <ItemCard
                      item={item}
                      index={index}
                      items={items}
                      isReadOnly={isReadOnly}
                      onToggleAbbr={handleToggleAbbr}
                      onMoveUp={() => handleMoveUp(index)}
                      onMoveDown={() => handleMoveDown(index)}
                    />
                    {!isReadOnly && (
                      <MiButton
                        startIcon={{ path: mdiMinus }}
                        color={BUTTON_COLORS.RED}
                        variant={BUTTON_VARIANTS.CONTAINED}
                        className={classes.rowActionBtn}
                        onClick={() => handleRemoveItem(item.id)}
                        disabled={items.length === 1}
                      />
                    )}
                  </Box>
                )}
              </Flipped>
            ))}
          </Flipper>
          {removedItems.length > 0 && (
            <Box className={classes.removedSection}>
              <Box className={classes.removedTextContainer}>
                <Typography variant="body2">Removed</Typography>
              </Box>
              {removedItems.map((item, index) => (
                <Box key={item.id} className={classes.sortableRow}>
                  <Typography variant="body2">{index + 1}.</Typography>
                  <Box className={classes.removedItemCard}>
                    <Typography variant="body2">
                      {item.label === "CategoryName" ? "Category" : item.label}
                    </Typography>
                  </Box>
                  {!isReadOnly && (
                    <MiButton
                      startIcon={{ path: mdiPlus }}
                      color={BUTTON_COLORS.PRIMARY}
                      variant={BUTTON_VARIANTS.CONTAINED}
                      className={classes.rowActionBtn}
                      onClick={() => handleAddBackItem(item)}
                    />
                  )}
                </Box>
              ))}
            </Box>
          )}
        </MiCard>
      </Box>
    </ConfigAccordionHeader>
  );
}

function ItemCard({
  item,
  index,
  items,
  isReadOnly,
  onToggleAbbr,
  onMoveUp,
  onMoveDown,
}) {
  const { classes } = useStyles();

  const isAbbr = item.value === "Abbr";

  return (
    <Box className={classes.itemCard}>
      <Typography variant="body2" sx={{ flex: 1 }}>
        {item.label === "CategoryName" ? "Category" : item.label}
      </Typography>
      {item.label !== "CategoryName" && (
        <Box className={classes.abbrToggle}>
          <MiButton
            title="Abbr."
            variant={
              isAbbr ? BUTTON_VARIANTS.CONTAINED : BUTTON_VARIANTS.OUTLINED
            }
            size="small"
            onClick={() => onToggleAbbr(item.id, true)}
            className={classes.abbrBtn}
            disabled={isReadOnly}
          />
          <MiButton
            title="Full"
            variant={
              isAbbr ? BUTTON_VARIANTS.OUTLINED : BUTTON_VARIANTS.CONTAINED
            }
            size="small"
            onClick={() => onToggleAbbr(item.id, false)}
            className={classes.abbrBtn}
            disabled={isReadOnly}
          />
        </Box>
      )}
      {!isReadOnly && (
        <MiReorderButton
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
          disableUp={index === 0}
          disableDown={index === items.length - 1}
          disableRipple
          disabled={isReadOnly}
        />
      )}
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  sortableCardContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  sortableRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  rowActionBtn: {
    borderRadius: "50%",
    padding: 0,
  },
  itemCard: {
    display: "flex",
    alignItems: "center",
    flex: 1,
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    border: `1px solid ${theme.palette.grey.accent}`,
    borderRadius: 4,
    backgroundColor: theme.palette.common.white,
    boxShadow: `0px 1px 2px ${theme.palette.grey.accent}`,
  },
  abbrToggle: {
    display: "flex",
    gap: theme.spacing(0.5),
  },
  abbrBtn: {
    borderRadius: 16,
    height: 24,
  },
  removedSection: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  removedTextContainer: {
    margin: theme.spacing(2, 0),
  },
  removedItemCard: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1, 2),
    border: `1px solid ${theme.palette.grey.accent}`,
    borderRadius: 4,
    backgroundColor: theme.palette.common.white,
    opacity: 0.4,
    boxShadow: `0px 1px 2px ${theme.palette.grey.accent}`,
  },
}));
